import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IUser } from '../../interfaces/IUser';
import { checkLogin, teamsLogin, logout } from './auth.thunks';
import { isTeamsApp, getEnvironment } from './auth.helpers';

interface AuthState {
  isLoggedIn: boolean;
  user: IUser;
  isLoading: boolean;
  error: string | null;
  isTeamsApp: boolean;
  environment: 'teams' | 'web';
}

const initialState: AuthState = {
  isLoggedIn: false,
  user: {} as IUser,
  isLoading: false,
  error: null,
  isTeamsApp: isTeamsApp(),
  environment: getEnvironment(),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<IUser>) => {
      state.isLoggedIn = true;
      state.user = action.payload;
      state.error = null;
    },
    setUser: (state, action: PayloadAction<IUser>) => {
      state.user = action.payload;
    },
    setEnvironment: (state, action: PayloadAction<'teams' | 'web'>) => {
      state.environment = action.payload;
      state.isTeamsApp = action.payload === 'teams';
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = true;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(checkLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = false;
        state.user = {} as IUser;
        state.error = action.payload as string;
      })
      .addCase(teamsLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(teamsLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.userData) {
          state.isLoggedIn = true;
          state.user = action.payload.userData;
        }
        state.error = null;
      })
      .addCase(teamsLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = false;
        state.user = {} as IUser;
        state.error = (action.payload as any)?.message || 'Teams login failed';
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoggedIn = false;
        state.user = {} as IUser;
        state.error = null;
      });
  },
});

export const { login, setUser, setEnvironment, clearError } = authSlice.actions;
export default authSlice.reducer;
